import { db } from "@/db/schema"
import { useLiveQueryImmediate } from "@/db/hooks"
import { uniqueIndexKeys } from "@/db/uniqueIndexKeys"

/**
 * Subscribe to all unique tags across the workspace.
 *
 * Used by tag autocomplete inputs. Returns a sorted array of all
 * distinct tags from `db.totodo` (excluding soft-deleted items).
 *
 * Uses `useLiveQueryImmediate` (non-suspending) so the component
 * never flashes during tag data loading.
 */
export function useAllTags(): [string[], boolean] {
  const [tags, , isLoading] = useLiveQueryImmediate(
    async () => {
      // Read distinct tags straight from the *tags multi-entry index (keys
      // only). This used to be a db.totodo.filter() over every object — ~23K
      // on a large account — re-run on every totodo write; while it ran,
      // IndexedDB held back every readwrite transaction on totodo, including
      // the user's own saves.
      const candidates = (await uniqueIndexKeys(db.totodo.orderBy("tags"))).filter(
        (k): k is string => typeof k === "string",
      )
      // The index also carries tags of soft-deleted objects. Keep a tag only
      // while a live object has it; each check stops at the first match.
      const live = await Promise.all(
        candidates.map((tag) =>
          db.totodo
            .where("tags")
            .equals(tag)
            .filter((obj) => obj.isDeleted === 0)
            .first(),
        ),
      )
      return candidates
        .filter((_, i) => live[i] !== undefined)
        .sort((a, b) => a.localeCompare(b))
    },
    ["all-tags"],
    [] as string[],
  )

  return [tags ?? [], isLoading]
}
